import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import ImageUploadField from './ImageUploadField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Toggle = ({ value, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3">
    <div>
      <p className="font-medium text-sm">{label}</p>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
    <button onClick={() => onChange(!value)} className={`relative w-11 h-6 rounded-full transition-colors ${value ? 'bg-black' : 'bg-gray-300'}`}>
      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${value ? 'translate-x-5' : ''}`} />
    </button>
  </div>
);

const ASPECT_RATIOS = [
  { value: '4/5', label: 'Portrait (4:5)' },
  { value: '3/4', label: 'Portrait (3:4)' },
  { value: '1/1', label: 'Square (1:1)' },
  { value: '4/3', label: 'Landscape (4:3)' },
  { value: '16/9', label: 'Video (16:9)' },
];

const SECTION_INFO = {
  tagline: { name: 'Philosophy Section', description: 'Large GIF on the left, text on the right.', maxGifs: 1 },
  gif_strip: { name: 'GIF / Photo Strip', description: 'Horizontal scrolling strip of images.', maxGifs: 6 },
  experience: { name: 'The Experience Section', description: 'Text about your approach with 2 GIF placeholders.', maxGifs: 2 },
  cta: { name: 'Call to Action Section', description: 'Final section with large GIF and inquiry button.', maxGifs: 1 },
};

const AboutSettingsPanel = () => {
  const [content, setContent] = useState(null);
  const [inquireContent, setInquireContent] = useState({ heading: 'Inquire', description: '', whatsapp_number: '+1-416-510-2424' });
  const [editorial, setEditorial] = useState([]);
  const [expandedSections, setExpandedSections] = useState([]);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [aboutRes, inquireRes, editorialRes] = await Promise.all([
        fetch(`${API_URL}/api/content/about-content`),
        fetch(`${API_URL}/api/content/inquire-content`),
        fetch(`${API_URL}/api/content/editorial`),
      ]);
      const aboutData = await aboutRes.json();
      const inquireData = await inquireRes.json();
      const editorialData = await editorialRes.json();

      setContent({ ...aboutData });
      if (inquireData) setInquireContent(prev => ({ ...prev, ...inquireData }));
      setEditorial(editorialData || []);
    } catch (e) {
      console.error('Failed to load:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async (section) => {
    setSaving(true);
    setMessage('');
    try {
      if (section === 'about') {
        await api.updateAboutContent(content);
        setMessage('About page saved!');
      } else if (section === 'inquire') {
        await api.updateInquireContent(inquireContent);
        setMessage('Inquire page saved!');
      }
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEditorialSection = async (sectionName) => {
    setSaving(true);
    try {
      const section = editorial.find(s => s.section_name === sectionName);
      await api.put(`/api/content/editorial/${sectionName}`, {
        title: section.title, subtitle: section.subtitle, description: section.description,
        is_active: section.is_active, gifs: section.gifs
      });
      setMessage('Section saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateEditorialField = (sectionName, field, value) => {
    setEditorial(prev => prev.map(s => s.section_name === sectionName ? { ...s, [field]: value } : s));
  };

  const handleGifUpload = async (sectionName, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [sectionName]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await api.upload('/api/upload/image', formData);
      if (result.url) {
        await api.post(`/api/content/editorial/${sectionName}/gif`, {
          id: '', url: result.url, label: file.name.replace(/\.[^/.]+$/, ''), aspect_ratio: '4/5', order: 0
        });
        await fetchData();
        setMessage('Image uploaded!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (e) {
      setMessage('Upload failed: ' + e.message);
    } finally {
      setUploading(prev => ({ ...prev, [sectionName]: false }));
    }
  };

  const removeGif = async (sectionName, gifId) => {
    try {
      await api.delete(`/api/content/editorial/${sectionName}/gif/${gifId}`);
      await fetchData();
    } catch (e) {
      setMessage('Failed to remove: ' + e.message);
    }
  };

  // Why Choose points
  const addPoint = () => setContent(prev => ({ ...prev, why_choose_points: [...(prev.why_choose_points || []), { title: '', description: '' }] }));
  const removePoint = (i) => setContent(prev => ({ ...prev, why_choose_points: prev.why_choose_points.filter((_, idx) => idx !== i) }));
  const updatePoint = (i, field, val) => {
    const pts = [...(content.why_choose_points || [])];
    pts[i] = { ...pts[i], [field]: val };
    setContent(prev => ({ ...prev, why_choose_points: pts }));
  };

  // Stats
  const addStat = () => setContent(prev => ({ ...prev, stats: [...(prev.stats || []), { number: '', label: '' }] }));
  const removeStat = (i) => setContent(prev => ({ ...prev, stats: prev.stats.filter((_, idx) => idx !== i) }));
  const updateStat = (i, field, val) => {
    const s = [...(content.stats || [])];
    s[i] = { ...s[i], [field]: val };
    setContent(prev => ({ ...prev, stats: s }));
  };

  // Description paragraphs
  const addParagraph = () => setContent(prev => ({ ...prev, description_paragraphs: [...(prev.description_paragraphs || []), ''] }));
  const removeParagraph = (i) => setContent(prev => ({ ...prev, description_paragraphs: prev.description_paragraphs.filter((_, idx) => idx !== i) }));

  if (loading || !content) return <div className="py-12 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">About Page</h2>
          <p className="text-sm text-gray-500 mt-1">All About page content and settings in one place</p>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('Error') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>{message}</div>
      )}

      {/* ─── 1. HERO & MAIN CONTENT ─── */}
      <div className="border rounded-lg p-6 space-y-5">
        <h3 className="text-lg font-semibold border-b pb-3">Hero & Main Content</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input type="text" value={content.heading} onChange={(e) => setContent({ ...content, heading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subheading</label>
            <input type="text" value={content.subheading} onChange={(e) => setContent({ ...content, subheading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
        </div>

        <ImageUploadField value={content.about_image_url || ''} onChange={(url) => setContent({ ...content, about_image_url: url })} folder="about" label="About Page Hero Image" />

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">Description Paragraphs</label>
            <button onClick={addParagraph} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">+ Add Paragraph</button>
          </div>
          {(content.description_paragraphs || []).map((para, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <textarea value={para} onChange={(e) => {
                const p = [...content.description_paragraphs];
                p[i] = e.target.value;
                setContent({ ...content, description_paragraphs: p });
              }} rows={2} className="flex-1 px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-black" />
              <button onClick={() => removeParagraph(i)} className="px-2 text-red-500 hover:bg-red-50 rounded self-start"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={() => save('about')} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} />{saving ? 'Saving...' : 'Save Content'}
          </button>
        </div>
      </div>

      {/* ─── 2. WHY CHOOSE CRYSTAL ─── */}
      <div className="border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold">Why Choose Crystal</h3>
          <button onClick={addPoint} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center gap-1"><Plus size={12} /> Add Point</button>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Section Heading</label>
          <input type="text" value={content.why_choose_heading || ''} onChange={(e) => setContent({ ...content, why_choose_heading: e.target.value })} className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black" />
        </div>

        {(content.why_choose_points || []).map((point, i) => (
          <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded">
            <span className="text-gray-400 text-sm mt-2 w-6">{i + 1}.</span>
            <div className="flex-1 space-y-2">
              <input type="text" value={point.title} onChange={(e) => updatePoint(i, 'title', e.target.value)} placeholder="Title" className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-black" />
              <textarea value={point.description} onChange={(e) => updatePoint(i, 'description', e.target.value)} placeholder="Description" rows={2} className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-black" />
            </div>
            <button onClick={() => removePoint(i)} className="px-2 text-red-500 hover:bg-red-50 rounded self-start"><Trash2 size={14} /></button>
          </div>
        ))}

        <div className="flex justify-end pt-2">
          <button onClick={() => save('about')} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ─── 3. STATS ─── */}
      <div className="border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold">Stats</h3>
          <button onClick={addStat} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded flex items-center gap-1"><Plus size={12} /> Add Stat</button>
        </div>
        {(content.stats || []).map((stat, i) => (
          <div key={i} className="flex items-center gap-3">
            <input type="text" value={stat.number} onChange={(e) => updateStat(i, 'number', e.target.value)} placeholder="200+" className="w-28 px-3 py-2 border rounded text-sm" />
            <input type="text" value={stat.label} onChange={(e) => updateStat(i, 'label', e.target.value)} placeholder="Weddings Filmed" className="flex-1 px-3 py-2 border rounded text-sm" />
            <button onClick={() => removeStat(i)} className="px-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="flex justify-end pt-2">
          <button onClick={() => save('about')} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ─── 4. CTA ─── */}
      <div className="border rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold border-b pb-3">CTA Section</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">CTA Text</label>
            <input type="text" value={content.cta_text || ''} onChange={(e) => setContent({ ...content, cta_text: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Button Text</label>
            <input type="text" value={content.cta_button_text || ''} onChange={(e) => setContent({ ...content, cta_button_text: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={() => save('about')} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} />{saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* ─── 5. SECTION VISIBILITY ─── */}
      <div className="border rounded-lg p-6 space-y-2">
        <h3 className="text-lg font-semibold border-b pb-3">Section Visibility</h3>
        <Toggle value={content.gif_strip_enabled !== false} onChange={(v) => setContent({ ...content, gif_strip_enabled: v })} label="GIF / Photo Strip" description="Horizontal scrolling gallery" />
        <Toggle value={content.experience_section_enabled !== false} onChange={(v) => setContent({ ...content, experience_section_enabled: v })} label="Experience Section" description="'More than just a video' section" />
        <Toggle value={content.faq_section_enabled !== false} onChange={(v) => setContent({ ...content, faq_section_enabled: v })} label="FAQ Section" description="Frequently Asked Questions" />
        <div className="flex justify-end pt-2">
          <button onClick={() => save('about')} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} />{saving ? 'Saving...' : 'Save Visibility'}
          </button>
        </div>
      </div>

      {/* ─── 6. EDITORIAL SECTIONS (GIFs/Images) ─── */}
      <div className="border rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold border-b pb-3">Page Images / GIFs</h3>
        <p className="text-sm text-gray-500">Manage images and GIFs that appear in each section of the About page.</p>

        {editorial.map((section) => {
          const info = SECTION_INFO[section.section_name] || { name: section.section_name, maxGifs: 4 };
          const isExpanded = expandedSections.includes(section.section_name);
          return (
            <div key={section.section_name} className="border rounded overflow-hidden">
              <button onClick={() => setExpandedSections(prev => isExpanded ? prev.filter(s => s !== section.section_name) : [...prev, section.section_name])} className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${section.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div>
                    <span className="font-medium text-sm">{info.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{section.gifs?.length || 0}/{info.maxGifs}</span>
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {isExpanded && (
                <div className="p-4 space-y-4 border-t">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Title</label>
                      <input type="text" value={section.title || ''} onChange={(e) => updateEditorialField(section.section_name, 'title', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Subtitle</label>
                      <input type="text" value={section.subtitle || ''} onChange={(e) => updateEditorialField(section.section_name, 'subtitle', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Description</label>
                    <textarea value={section.description || ''} onChange={(e) => updateEditorialField(section.section_name, 'description', e.target.value)} rows={2} className="w-full px-2 py-1.5 border rounded text-sm" />
                  </div>

                  <Toggle value={section.is_active} onChange={(v) => updateEditorialField(section.section_name, 'is_active', v)} label="Visible on page" />

                  <div className="grid grid-cols-3 gap-3">
                    {section.gifs?.map((gif, idx) => (
                      <div key={gif.id || idx} className="relative group">
                        <div className="bg-gray-100 rounded overflow-hidden" style={{ aspectRatio: gif.aspect_ratio || '4/5' }}>
                          <img src={gif.url} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-2">
                          <select value={gif.aspect_ratio || '4/5'} onChange={async (e) => {
                            try {
                              await api.put(`/api/content/editorial/${section.section_name}/gif/${gif.id}`, { ...gif, aspect_ratio: e.target.value });
                              await fetchData();
                            } catch {}
                          }} className="px-1 py-0.5 text-[10px] bg-white rounded" onClick={(e) => e.stopPropagation()}>
                            {ASPECT_RATIOS.map(ar => <option key={ar.value} value={ar.value}>{ar.label}</option>)}
                          </select>
                          <button onClick={() => removeGif(section.section_name, gif.id)} className="p-1.5 bg-red-500 text-white rounded-full"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    ))}
                    {(section.gifs?.length || 0) < info.maxGifs && (
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center hover:border-gray-400 transition-colors" style={{ aspectRatio: '4/5' }}>
                          {uploading[section.section_name] ? (
                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <div className="text-center text-gray-400"><Upload size={18} /><span className="text-[10px] block mt-1">Upload</span></div>
                          )}
                        </div>
                        <input type="file" accept="image/*,.gif" className="hidden" onChange={(e) => handleGifUpload(section.section_name, e.target.files[0])} />
                      </label>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button onClick={() => saveEditorialSection(section.section_name)} disabled={saving} className="px-4 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1">
                      <Save size={12} />{saving ? 'Saving...' : 'Save Section'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── 7. INQUIRE PAGE ─── */}
      <div className="border rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold border-b pb-3">Inquire Page Content</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input type="text" value={inquireContent.heading} onChange={(e) => setInquireContent({ ...inquireContent, heading: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">WhatsApp / Call Number</label>
            <input type="text" value={inquireContent.whatsapp_number} onChange={(e) => setInquireContent({ ...inquireContent, whatsapp_number: e.target.value })} className="w-full px-3 py-2 border rounded" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea value={inquireContent.description} onChange={(e) => setInquireContent({ ...inquireContent, description: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded" />
        </div>
        <ImageUploadField value={inquireContent.image_url || ''} onChange={(url) => setInquireContent({ ...inquireContent, image_url: url })} folder="inquire" label="Contact Image" />
        <div className="flex justify-end pt-2">
          <button onClick={() => save('inquire')} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
            <Save size={14} />{saving ? 'Saving...' : 'Save Inquire'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutSettingsPanel;
